import { useState } from "react";
import { StepIndicator } from "./indicator";
import {
  destinationConnectors,
  sourceConnectors,
} from "../../../pages/connectors/connector/data";
import { Connector } from "../../types";
import { useNavigate } from "react-router-dom";
import { SetupGuide } from "./setup-guide";
import { Navigation } from "./navigation";
import { ConnectorSelection } from "./connector-seletion";

export default function NewConnectionFlow() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedSource, setSelectedSource] = useState<Connector | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const [selectedConnectorForGuide, setSelectedConnectorForGuide] =
    useState<Connector | null>(null);
  const [selectedDestination, setSelectedDestination] =
    useState<Connector | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    syncMode: "realtime",
    backfillHistorical: true,
    table: "events",
  });
  const [connectionTested, setConnectionTested] = useState(false); // New state for connection test

  const steps = [
    "Select Source",
    "Configure Source",
    "Select Destination",
    "Configure Connection",
  ];

  function createConnectors() {
    navigate("/connectors");
  }

  const filteredSources = sourceConnectors.filter((source) =>
    source.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDestinations = destinationConnectors.filter((dest) =>
    dest.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const nextStep = (): void => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
      setShowGuide(false);
      setConnectionTested(false); // Reset connection test status when moving to next step
    }
  };

  const prevStep = (): void => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setShowGuide(false);
      setConnectionTested(false); // Reset connection test status when going back
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return showGuide ? connectionTested : selectedSource;
      case 2:
        return connectionTested; // Only proceed if connection is tested
      case 3:
        return selectedDestination;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleSubmit = () => {
    alert("Connection created successfully!");
    navigate("/connectors");
  };

  const handleConfigure = (id: string) => {};

  const handleShowGuide = (connector: Connector): void => {
    setSelectedConnectorForGuide(connector);
    setShowGuide(true);
    setConnectionTested(false); // ✅ force test again whenever guide is opened
  };

  const handleCloseGuide = (): void => {
    setShowGuide(false);
    setSelectedConnectorForGuide(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };

  const handleBackNavigation = (): void => {
    if (currentStep === 1) {
      window.history.back();
    } else {
      prevStep();
    }
  };

  const handleTestConnection = (): void => {
    setTimeout(() => {
      alert("Connection test successful!");
      setConnectionTested(true);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-white flex">
      <div
        className={`transition-all duration-300 ${
          showGuide ? "w-1/2" : "w-full"
        }`}
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center space-x-2 text-sm text-gray-500 mb-4">
            <button onClick={createConnectors}>Connections</button>
            <span>/</span>
            <span>New connection</span>
          </div>

          <StepIndicator currentStep={currentStep} steps={steps} />
        </div>

        {/* Content */}
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Step 1: Select Source */}
          {currentStep === 1 && (
            <ConnectorSelection
              title="Select Source"
              description="Choose a data source to connect from"
              connectors={filteredSources}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              selectedConnector={selectedSource}
              onSelectConnector={setSelectedSource}
              onShowGuide={handleShowGuide}
              onConfigure={handleConfigure}
              placeholder="Search sources..."
            />
          )}

          {/* Step 2: Configure Source */}
          {currentStep === 2 && (
            <div>
              <div className="mb-8">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-2xl">{selectedSource?.icon}</span>
                  <h1 className="text-2xl font-semibold text-gray-900">
                    Configure {selectedSource?.name}
                  </h1>
                </div>
                <p className="text-gray-600">
                  Set up your {selectedSource?.name} connection
                </p>
              </div>

              <div className="max-w-2xl">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Connection Details
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Connection Name
                      </label>
                      <input
                        type="text"
                        placeholder={`${selectedSource?.name} Connection`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        API Key / Token
                      </label>
                      <input
                        type="password"
                        placeholder="Enter your API key"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Base URL (optional)
                      </label>
                      <input
                        type="url"
                        placeholder="https://api.example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleTestConnection}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                  >
                    Test Connection
                  </button>

                  {connectionTested && (
                    <div className="mt-3 text-green-600 font-medium">
                      ✓ Connection test successful
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Select Destination */}
          {currentStep === 3 && (
            <ConnectorSelection
              title="Select Destination"
              description="Choose where to send your data"
              connectors={filteredDestinations}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              selectedConnector={selectedDestination}
              onSelectConnector={setSelectedDestination}
              onShowGuide={handleShowGuide}
              onConfigure={handleConfigure}
              placeholder="Search destinations..."
            />
          )}

          {/* Step 4: Configure Connection */}
          {currentStep === 4 && (
            <div>
              <div className="mb-8">
                <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                  Configure Connection
                </h1>
                <p className="text-gray-600">
                  Set up sync settings and review your connection
                </p>
              </div>

              <div className="max-w-4xl space-y-8">
                {/* Connection Summary */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Connection Summary
                  </h3>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">{selectedSource?.icon}</span>
                        <span className="font-medium">
                          {selectedSource?.name}
                        </span>
                      </div>
                      <div className="text-gray-400">→</div>
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">
                          {selectedDestination?.icon}
                        </span>
                        <span className="font-medium">
                          {selectedDestination?.name}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sync Settings */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-6">
                    Sync Settings
                  </h3>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Table / Dataset
                      </label>
                      <select
                        value={formData.table}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            table: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="events">events</option>
                        <option value="users">users</option>
                        <option value="products">products</option>
                        <option value="orders">orders</option>
                      </select>
                    </div>

                    <div>
                      <h4 className="text-base font-medium text-gray-900 mb-4">
                        Sync Mode
                      </h4>
                      <div className="space-y-3">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.syncMode === "realtime"}
                            onChange={() =>
                              setFormData((prev) => ({
                                ...prev,
                                syncMode: "realtime",
                              }))
                            }
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="ml-3 text-sm text-gray-900">
                            Realtime (event streaming)
                          </span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.syncMode === "scheduled"}
                            onChange={() =>
                              setFormData((prev) => ({
                                ...prev,
                                syncMode: "scheduled",
                              }))
                            }
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="ml-3 text-sm text-gray-900">
                            Scheduled (batch sync)
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-base font-medium text-gray-900">
                            Backfill historical data
                          </h4>
                          <p className="text-sm text-gray-500">
                            Include past events when syncing for the first time.
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.backfillHistorical}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                backfillHistorical: e.target.checked,
                              }))
                            }
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <Navigation
            currentStep={currentStep}
            onBack={handleBackNavigation}
            onContinue={nextStep}
            onSubmit={handleSubmit}
            canProceed={canProceed()}
            isLastStep={currentStep === 4}
          />
        </div>
      </div>
      {showGuide && selectedConnectorForGuide && (
        <div className="w-1/2 border-l border-gray-300">
          <SetupGuide
            connector={selectedConnectorForGuide}
            onClose={handleCloseGuide}
            onTestConnection={(success) => {
              if (success) {
                setConnectionTested(true);
              }
            }}
          />
        </div>
      )}
    </div>
  );
}
